import RAPIER from '@dimforge/rapier3d-compat';
import type { PerspectiveCamera } from 'three';

/** S-008 collision sphere. */
export const BOOM_RADIUS = 0.18;
/** Combat min boom — hard clip floor so the camera cannot rest inside a mesh. */
export const BOOM_MIN = 0.55;
export const BOOM_LENGTH = 2.6;
export const SHOULDER = 0.5;
export const EYE_HEIGHT = 1.55;
export const SNEAK_EYE = 0.95;

const IDENTITY = { x: 0, y: 0, z: 0, w: 1 };

export type BoomPoint = { x: number; y: number; z: number };

/**
 * Sphere-cast the TPS boom from the shoulder toward the desired camera.
 * If the shoulder is already inside a wall, restart from the player head.
 */
export function resolveCameraBoom(
  world: RAPIER.World,
  shoulder: BoomPoint,
  desired: BoomPoint,
  fallback: BoomPoint,
  excludeBody: RAPIER.RigidBody,
  ball: RAPIER.Ball,
): BoomPoint {
  const startInside = world.intersectionWithShape(
    shoulder,
    IDENTITY,
    ball,
    undefined,
    undefined,
    undefined,
    excludeBody,
  );
  return castBoom(world, startInside ? fallback : shoulder, desired, excludeBody, ball);
}

export function applyCameraBoom(
  camera: PerspectiveCamera,
  world: RAPIER.World,
  player: BoomPoint,
  feetY: number,
  yaw: number,
  pitch: number,
  sneak: boolean,
  excludeBody: RAPIER.RigidBody,
  ball: RAPIER.Ball,
): { camInsideMesh: boolean } {
  const sinY = Math.sin(yaw);
  const cosY = Math.cos(yaw);
  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  const rightX = cosY;
  const rightZ = -sinY;
  const forwardX = -sinY;
  const forwardZ = -cosY;
  const eye = sneak ? SNEAK_EYE : EYE_HEIGHT;

  const targetX = player.x + rightX * SHOULDER;
  const targetY = feetY + eye;
  const targetZ = player.z + rightZ * SHOULDER;
  const lookX = forwardX * cosP;
  const lookY = sinP;
  const lookZ = forwardZ * cosP;
  const shoulder = { x: targetX, y: targetY, z: targetZ };
  const desired = {
    x: targetX - lookX * BOOM_LENGTH,
    y: targetY - lookY * BOOM_LENGTH,
    z: targetZ - lookZ * BOOM_LENGTH,
  };
  const resolved = resolveCameraBoom(
    world,
    shoulder,
    desired,
    { x: player.x, y: targetY, z: player.z },
    excludeBody,
    ball,
  );
  camera.position.set(resolved.x, resolved.y, resolved.z);
  camera.lookAt(targetX, targetY, targetZ);

  const inside = world.intersectionWithShape(
    resolved,
    IDENTITY,
    ball,
    undefined,
    undefined,
    undefined,
    excludeBody,
  );
  return { camInsideMesh: Boolean(inside) };
}

function castBoom(
  world: RAPIER.World,
  origin: BoomPoint,
  desired: BoomPoint,
  excludeBody: RAPIER.RigidBody,
  ball: RAPIER.Ball,
): BoomPoint {
  const dx = desired.x - origin.x;
  const dy = desired.y - origin.y;
  const dz = desired.z - origin.z;
  const dist = Math.hypot(dx, dy, dz);
  if (dist <= BOOM_MIN) {
    return { x: desired.x, y: desired.y, z: desired.z };
  }

  const inv = 1 / dist;
  const dir = { x: dx * inv, y: dy * inv, z: dz * inv };
  const hit = world.castShape(
    origin,
    IDENTITY,
    dir,
    ball,
    0,
    dist,
    true,
    undefined,
    undefined,
    undefined,
    excludeBody,
  );

  let travel = dist;
  if (hit) {
    travel = Math.min(dist, Math.max(0, hit.time_of_impact));
  }
  travel = Math.max(BOOM_MIN, travel);
  return {
    x: origin.x + dir.x * travel,
    y: origin.y + dir.y * travel,
    z: origin.z + dir.z * travel,
  };
}
